import { fetchRequest } from "@/plugins/fetchRequest";
import functions from "@/functions";

const actions = {
  status({ commit }: StoreProp, { campaignId, campaign }: UnknownObj) {
    return new Promise((resolve, reject) => {
      fetchRequest({
        url: `/campaigns/status/${campaignId}`
      })
        .then((responseInfo: Campaign = {}) => {
          campaign.stayFilm = { ...(campaign.stayFilm || {}), ...responseInfo };

          if (commit) {
            commit("SET_ACTIVE", { ...campaign });
            resolve(campaign);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  load({ commit }: StoreProp, { campaignId }: UnknownObj) {
    return new Promise((resolve, reject) => {
      fetchRequest({
        url: `/campaigns/${campaignId}`
      })
        .then((responseInfo = {}) => {
          const campaign: Campaign = functions.campaingDefaults(responseInfo);

          if (commit) {
            commit("SET_ACTIVE", campaign);
            resolve(campaign);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  create({ commit }: StoreProp, { payload = {} }: UnknownObj) {
    return new Promise((resolve, reject) => {
      fetchRequest({
        url: "/campaigns",
        method: "post",
        body: payload as BodyInit
      })
        .then((responseInfo = {}) => {
          responseInfo = functions.campaingDefaults(responseInfo);

          if (commit) {
            commit("SET_ACTIVE", responseInfo);
            resolve(responseInfo);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  update(
    { commit }: StoreProp,
    { payload = {}, campaignId, reset = false }: UnknownObj
  ) {
    return new Promise((resolve, reject) => {
      fetchRequest({
        url: `/campaigns/${campaignId}`,
        method: "patch",
        body: payload as BodyInit
      })
        .then((campaignData: Campaign = {}) => {
          campaignData = functions.campaingDefaults(campaignData);

          if (commit) {
            commit("SET_ACTIVE", campaignData);

            if (reset) {
              // reset products so user gets a new one
              // in the products/media selection section again
              commit("RESET_PRODUCTS");
            }

            resolve(campaignData);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  remove({ commit }: StoreProp, { campaignId }: UnknownObj) {
    return new Promise((resolve, reject) => {
      fetchRequest({
        url: `/campaigns/${campaignId}`,
        method: "delete"
      })
        .then(() => {
          if (commit) {
            resolve(true);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  list({ commit }: StoreProp, config: UnknownObj) {
    const { orderBy = "id", order, name, status } = config;

    const params: string[] = [];

    params.push(`orderBy=${orderBy}`);

    if (order) {
      params.push(`order=${order}`);
    }

    if (name) {
      params.push(`name=${name}`);
    }

    if (status) {
      params.push(`status=${status}`);
    }

    return new Promise((resolve, reject) => {
      fetchRequest({
        url: `/campaigns?${params.join("&")}`
      })
        .then((responseInfo = {}) => {
          if (commit) {
            commit("LIST_UPDATE", responseInfo.data);
            resolve(responseInfo);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  segments({ commit }: StoreProp) {
    return new Promise((resolve, reject) => {
      fetchRequest({
        url: "/market-segments"
      })
        .then((responseInfo = {}) => {
          const segments = responseInfo.segments || responseInfo || [];

          if (commit) {
            commit("UPDATE_MARKET_SEGMENTS", segments);
            resolve(segments);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  locations({ commit }: StoreProp, config: UnknownObj) {
    const { qname = "", limit = 0, offset = 0, orderBy, order } = config;

    const params: string[] = [];

    if (qname) {
      params.push(`qname=${qname}`);
    }

    if (orderBy) {
      params.push(`orderBy=${orderBy}`);
    }

    if (order) {
      params.push(`order=${order}`);
    }

    if (limit) {
      params.push(`limit=${limit}`);
    }

    if (offset) {
      params.push(`offset=${offset}`);
    }

    return new Promise((resolve, reject) => {
      fetchRequest({ url: `/locations?${params.join("&")}` })
        .then((responseInfo = {}) => {
          const locations = responseInfo.data || responseInfo || [];

          if (commit) {
            commit("UPDATE_LOCATION_RESULTS", locations);
            resolve(responseInfo);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  checkAvailability(
    { commit }: StoreProp,
    { campaignId, products = {} }: UnknownObj
  ) {
    const payload = { products } as UnknownObj;

    return new Promise((resolve, reject) => {
      fetchRequest({
        url: `/campaigns/${campaignId}/suggestion/validate`,
        method: "post",
        body: payload as BodyInit
      })
        .then(responseInfo => {
          const responseData = responseInfo.data || responseInfo;
          if (commit) {
            resolve(responseData);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  broadcasters({ commit }: StoreProp, config: UnknownObj) {
    const { broadcasterId = "1", update = false } = config;

    if (!update && commit) {
      commit("UPDATE_LOCATIONS", {
        locations: [],
        broadcasterName: "",
        update
      });
    }

    return new Promise((resolve, reject) => {
      fetchRequest({ url: `/broadcasters/${broadcasterId}` })
        .then((responseInfo = {}) => {
          const { locations, name: broadcasterName } =
            responseInfo.data || responseInfo || [];

          if (commit) {
            commit("UPDATE_LOCATIONS", { locations, broadcasterName, update });
            resolve(locations);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  investment({ commit }: StoreProp, config: UnknownObj = {}) {
    const { campaignId } = config;

    return new Promise((resolve, reject) => {
      fetchRequest({
        url: `campaigns/${campaignId}/investment/`
      })
        .then((responseInfo = {}) => {
          let { minPrice } = responseInfo.data || responseInfo;
          minPrice = minPrice || 1500;

          if (commit) {
            commit("UPDATE_INVESTMENT", minPrice);
            resolve(minPrice);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  products({ commit }: StoreProp, config: UnknownObj = {}) {
    const { campaignId, limit, offset, orderBy } = config;

    return new Promise((resolve, reject) => {
      fetchRequest({
        url: `/campaigns/${campaignId}/products?limit=${limit}&offset=${offset}&orderBy=${orderBy}`
      })
        .then((responseInfo = {}) => {
          if (commit) {
            commit("PRODUCTS_LIST", responseInfo.data || []);
            resolve(responseInfo.data);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  suggestion({ commit }: StoreProp, config: UnknownObj = {}) {
    const { campaignId, notProducts = "", reset = false } = config;

    return new Promise((resolve, reject) => {
      fetchRequest({
        url: `/campaigns/${campaignId}/suggestion?notProducts=${notProducts}`
      })
        .then((responseInfo = {}) => {
          if (commit) {
            commit("PRODUCTS_SUGGESTION", { ...responseInfo, reset });
            resolve(responseInfo);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  mediaPlan({ commit }: StoreProp, payload: UnknownObj = {}) {
    const { campaign, format } = payload;

    const url = `/campaigns/planDetail?format=${format}`;

    campaign.date = functions.formatDateUTC("");

    return new Promise((resolve, reject) => {
      fetchRequest({ url, method: "post", body: campaign as BodyInit })
        .then((responseInfo = {}) => {
          if (commit) {
            resolve(responseInfo);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  allBroadcasters({ commit }: StoreProp) {
    return new Promise((resolve, reject) => {
      fetchRequest({
        url: "/broadcasters"
      })
        .then((responseInfo = {}) => {
          const broadcasters = responseInfo.data || responseInfo;

          if (commit) {
            commit("UPDATE_BROADCASTERS", broadcasters);
            resolve(broadcasters);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  authPayment({ commit }: StoreProp) {
    return new Promise((resolve, reject) => {
      fetchRequest({
        url: "https://api-payment.sbtads.online/api/v1/auth",
        method: "get"
      })
        .then((responseInfo = {}) => {
          if (commit) {
            resolve(responseInfo);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  coupon({ commit }: StoreProp, { couponCode }: UnknownObj) {
    return new Promise((resolve, reject) => {
      fetchRequest({
        url: `/coupon/${couponCode}`,
        method: "get"
      })
        .then((responseInfo = {}) => {
          if (commit) {
            const response = responseInfo.data || responseInfo;
            resolve(response);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  },
  payment({ commit }: StoreProp, { payload = {}, campaignId }: UnknownObj) {
    return new Promise((resolve, reject) => {
      fetchRequest({
        url: `/campaigns/${campaignId}/payment`,
        method: "post",
        body: payload as BodyInit
      })
        .then((responseInfo = {}) => {
          if (commit) {
            commit("RESET_CAMPAIGN");
            resolve(responseInfo);
          }
        })
        .catch((err: UnknownObj) => {
          reject(err);
        });
    });
  }
};

export default actions;
